import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";
import { api, fmtErr } from "@/api";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F1EB] flex items-center justify-center px-6 py-16" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-10" data-testid="forgot-home-link">
          <Logo size={44} />
        </Link>
        <div className="bg-white border border-[#E5E2D9] p-8 sm:p-10 shadow-[8px_8px_0_0_#E5E2D9]">
          {done ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="w-16 h-16 mx-auto bg-[#F7F0D8] flex items-center justify-center mb-6">
                <MailCheck size={28} className="text-[#B5952F]" />
              </div>
              <h1 className="font-display text-2xl font-bold text-[#1A1A1A]">{t("fp_title")}</h1>
              <p className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">{t("fp_sent")}</p>
              <Link
                to="/login"
                className="inline-block mt-8 bg-[#1A1A1A] text-[#D4AF37] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300"
                data-testid="forgot-back-login-button"
              >
                {t("fp_back")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-[#1A1A1A]">{t("fp_title")}</h1>
              <p className="text-sm text-[#5C5C5C] mt-2 mb-8">{t("fp_sub")}</p>
              {error && (
                <div className="mb-6 border border-[#E03131] bg-[#E03131]/5 text-[#E03131] text-sm font-semibold px-4 py-3" data-testid="forgot-error">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C] mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-[#E5E2D9] bg-[#FDFCF8] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1A1A1A] text-[#D4AF37] py-4 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-50"
                  data-testid="forgot-submit-button"
                >
                  {loading ? "..." : t("fp_send")}
                </button>
              </form>
              <p className="text-sm text-[#5C5C5C] mt-8 text-center">
                <Link to="/login" className="font-bold text-[#B5952F] hover:underline" data-testid="forgot-login-link">
                  {t("fp_back")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
